import React from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {orderBy} from 'lodash'
import Box from '@mui/material/Box'
import TakeAssessment from 'components/quiz/take'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import FullScreenDialog from 'components/FullScreenDialog'
import Section from 'components/quiz/take/Section'
import PreviewAssessment from 'components/quiz/preview'
import PreviewResultsAssessment from 'components/quiz/preview-results'
import Question from 'components/quiz/take/Question'
import {createPost} from 'helpers/command'

const getTest = createPost('quiz', 'getImportedAssessmentTest')
const startTest = createPost('quiz', 'startImportedAssessmentTest')
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const saveTest = createPost('quiz', 'saveImportedAssessmentTestAnswers')

export default function ImportedTeacherTest ({context}) {
  const history = useHistory()
  const location = useLocation()
  const [attempt, setAttempt] = React.useState(null)
  const [answers, setAnswers] = React.useState({})
  const [position, setPosition] = React.useState(0)
  const stageParam = new URLSearchParams(location.search).get('stage')
  const stage = ['take', 'results'].includes(stageParam) ? stageParam : 'preview'
  const [error, setError] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [starting, setStarting] = React.useState(false)
  const id = context.attemptId
  React.useEffect(() => {
    const n = Number(new URLSearchParams(location.search).get('previewPosition') || 0)
    if (Number.isInteger(n) && n >= 0) setPosition(n)
  }, [location.search])

  React.useEffect(() => {
    let active = true
    setAttempt(null)
    setError('')
    setPosition(0)
    getTest({id}).then(result => {
      if (!active) return
      const data = Array.isArray(result) ? result[0] : result
      if (!data || data.id !== id) throw new Error('Attempt not found')
      setAttempt(data)
      setAnswers(Object.values(data.content && data.content.sections || {}).reduce((all, section) => {
        (section.questions || []).forEach(question => { all[question.id] = (data.answers || {})[question.id] || {} })
        return all
      }, {}))
    }).catch(() => { if (active) setError('Unable to load imported assessment preview.') })
    return () => { active = false }
  }, [id])

  const sourceId = new URLSearchParams(location.search).get('sourceId') || (attempt && attempt.assessmentId)
  const back = new URLSearchParams(location.search).get('returnTo') || (location.state && location.state.back)
  const returnTo = back && back.startsWith('/') && !back.startsWith('//') ? back : `/${context.languageId}/${context.role}/my-courses`
  const route = (stageName, index = 0, attemptId = id) => `/${context.languageId}/${context.role}/quiz/imported-test/${attemptId}?stage=${stageName}&previewPosition=${index}&sourceId=${encodeURIComponent(sourceId || '')}&returnTo=${encodeURIComponent(returnTo)}`
  async function startFreshTest() {
    if (starting) return
    if (!sourceId || !uuid.test(sourceId)) { setError('Imported assessment source unavailable. Reopen from the lesson.'); return }
    setStarting(true)
    setError('')
    try {
      const response = await startTest({id: sourceId})
      const newId = Array.isArray(response) ? response[0] : response
      if (typeof newId !== 'string' || !uuid.test(newId) || newId === id) throw new Error('Unable to create a new attempt')
      history.replace(route('take', 0, newId))
    } catch (e) { setError('Unable to start a fresh imported assessment test.') }
    finally { setStarting(false) }
  }
  const close = () => history.push(returnTo)
  const content = attempt && attempt.content || {}
  const sections = orderBy(Object.values(content.sections || {}).map(section => ({...section, questions: orderBy(section.questions || [], 'index')})), 'index')
  const pages = sections.flatMap(section => [{section, question: null}, ...section.questions.map(question => ({section, question}))])
  const current = pages[position]
  const readOnly = Boolean(attempt && attempt.finished)
  const previewContext = {
    ...context,
    importedMath: true,
    assessment: id,
    quizAssessment: {id, title: content.title, type: content.type, owned: true},
    quizSections: sections,
    quizQuestions: Object.fromEntries(sections.map(section => [section.id, section.questions])),
    quizSearch: {assessment: false},
    sectionId: current && current.section.id,
    questionId: current && current.question && current.question.id,
    link: ({sectionId, questionId} = {}) => {
      const target = pages.findIndex(page => page.section.id === (sectionId || (current && current.section.id)) && (page.question ? page.question.id : null) === (questionId || null))
      return route('preview', target < 0 ? 0 : target)
    }
  }


  // The legacy question controls expect the same updateState/saveAnswers contract as TakeAssessment.
  const adapter = {
    ...context,
    importedMath: true,
    assessment: id,
    sectionId: current && current.section.id,
    questionId: current && current.question && current.question.id,
    quizAssessmentToTake: {...attempt, assessmentId: id, content, answers},
    quizSearch: {'assessment-to-take': false},
    link: ({sectionId, questionId} = {}) => {
      const target = pages.findIndex(page => page.section.id === (sectionId || (current && current.section.id)) && (page.question ? page.question.id : null) === (questionId || null))
      return route('take', target < 0 ? 0 : target)
    },
    updateState: async patch => {
      const changes = patch.quizAssessmentToTake && patch.quizAssessmentToTake.answers || {}
      const next = {...answers}
      Object.entries(changes).forEach(([questionId, fields]) => {
        const updated = {...(next[questionId] || {})}
        Object.entries(fields).forEach(([key, instruction]) => {
          if (instruction && Object.prototype.hasOwnProperty.call(instruction, '$set')) updated[key] = instruction.$set
        })
        next[questionId] = updated
      })
      setAnswers(next)
      return {quizAssessmentToTake: {answers: next}}
    },
    saveAnswers: async ({answers: next}) => {
      if (readOnly) return
      setSaving(true)
      try {
        const response = await saveTest({id, answers: next, finalize: false})
        if ((Array.isArray(response) ? response[0] : response) !== id) throw new Error('Save rejected')
      } catch(e) { setError('Unable to save answers.') }
      finally { setSaving(false) }
    }
  }

  async function finalize() {
    setSaving(true)
    setError('')
    try {
      const response = await saveTest({id, answers, finalize: true})
      if ((Array.isArray(response) ? response[0] : response) !== id) throw new Error('Finalize rejected')
      const result = await getTest({id})
      const updated = Array.isArray(result) ? result[0] : result
      if (!updated || !updated.finished) throw new Error('Finalization not confirmed')
      setAttempt(updated)
      history.replace(route('results', 0))
      return updated
    } catch(e) {
      setError('Unable to finalize assessment.')
      throw e
    } finally { setSaving(false) }
  }

  if (attempt && stage === 'results') return <PreviewResultsAssessment
    context={{...context, importedMath: true, assessment: id,
      quizAssessmentResults: {...attempt, assessmentId: id},
      quizSearch: {'assessment-results': false}}}
    imported={{closeLink: route('preview', 0)}}
  />

  if (attempt && stage === 'preview') return <PreviewAssessment
    context={previewContext}
    imported={{onStart: startFreshTest, closeLink: returnTo}}
  />

  if (attempt && stage === 'take') return <TakeAssessment
    context={adapter}
    key={id}
    imported={{saveAnswers: adapter.saveAnswers, finalize, readOnly, closeLink: route('preview', 0)}}
  />

  return <FullScreenDialog open={true}><Box sx={{padding:3}}>
    {error ? <p role="alert">{error}</p> : <p role="status">Loading imported assessment…</p>}
    <Button onClick={close}>Close</Button>
  </Box></FullScreenDialog>
}
